/**
 * Admin V3 — Financial / Corporate / Entreprises — top-level page
 *
 * Mounted at: /admin-v3/corporate (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Corporate account directory (read-only).
 * Source: GET /api/enterprises (enterprise.py:113-158), enriched with
 * total_bookings + total_spent per account.
 *
 * NOTE — path namespace: This endpoint is mounted at /api/enterprises
 * (NOT /api/admin/enterprises). The enterprise router has no /admin prefix.
 * This is the first V3 module to call a non-/admin path; useAdminQuery
 * passes it through unchanged via the API_URL + endpoint concatenation.
 *
 * SCOPE FOR PHASE 2.12:
 *   - GET /api/enterprises (NO server-side filters — endpoint accepts no params)
 *   - Client-side search over loaded page (name, email, SIRET, contact_name)
 *   - Client-side status filter (active / suspended / inactive) over loaded page
 *   - Per-row expansion with address, billing email/address, base_rate, discount
 *   - Row click → /admin-v3/corporate/:id (placeholder)
 *
 * MUTATIONS (AV3 A-6, all RBAC-gated; backend is the single source of truth):
 *   - Create corporate client       → POST  /enterprises            (enterprise:create)
 *   - Edit corporate client         → PATCH /enterprises/{id}       (enterprise:update)
 *   - Activate / Suspend            → PATCH /enterprises/{id}{status}(enterprise:update)
 *   - Employee management/invitation→ /corporate/{id}/employees*    (employee:*)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Building2, Plus } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import { Button } from '../../../components/ui/button';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import usePermissions from '../../shared/hooks/usePermissions';
import { t, DEFAULT_LOCALE } from '../../i18n';
import CorporateListSearchBar from './CorporateListSearchBar';
import CorporateListTable from './CorporateListTable';
import CorporateCreateModal from './CorporateCreateModal';
import CorporateEditModal from './CorporateEditModal';
import CorporateEmployeesModal from './CorporateEmployeesModal';

const SEARCH_DEBOUNCE_MS = 300;
const BACKEND_HARD_CAP = 100;  // backend to_list(100) — enterprise.py:123

const CorporateListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const { can } = usePermissions();
  const [createOpen, setCreateOpen] = useState(false);
  const [editEnterprise, setEditEnterprise] = useState(null);
  const [employeesEnterprise, setEmployeesEnterprise] = useState(null);

  // Backend accepts NO query params. All filtering is client-side.
  const { data, loading, error, refetch } = useAdminQuery('/enterprises');

  // /enterprises returns a bare array, not a {enterprises, count} envelope.
  const allEnterprises = Array.isArray(data) ? data : [];

  // Client-side filtering (status + search) over the loaded page.
  const enterprises = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return allEnterprises.filter((e) => {
      if (status && (e.status || 'active') !== status) return false;
      if (!q) return true;
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q) ||
        (e.siret || '').toLowerCase().includes(q) ||
        (e.contact_name || '').toLowerCase().includes(q)
      );
    });
  }, [allEnterprises, debouncedSearch, status]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(status);
  const isAtBackendCap = allEnterprises.length === BACKEND_HARD_CAP;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('corporate.title', locale)}
      icon={Building2}
      iconClass="bg-indigo-100 text-indigo-600"
    >
      <div className="space-y-4">
        {can('enterprise:create') && (
          <div className="flex justify-end">
            <Button variant="default" size="sm" onClick={() => setCreateOpen(true)} data-testid="adminv3-corporate-create-action">
              <Plus className="h-3.5 w-3.5 mr-1.5" />{t('corporate.create.action', locale)}
            </Button>
          </div>
        )}

        <CorporateListSearchBar
          status={status}
          onStatusChange={setStatus}
          search={search}
          onSearchChange={setSearch}
          resultsCount={enterprises.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <CorporateListTable
          enterprises={enterprises}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          onEdit={setEditEnterprise}
          onManageEmployees={setEmployeesEnterprise}
          onRefresh={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-corporate-cap-hint"
          >
            {t('corporate.cap.hint', locale).replace('{n}', String(BACKEND_HARD_CAP))}
          </p>
        )}

        <CorporateCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onDone={refetch}
          locale={locale}
        />
        {/* Mount only when open so the form state initialises from the row. */}
        {editEnterprise && (
          <CorporateEditModal
            open
            enterprise={editEnterprise}
            onClose={() => setEditEnterprise(null)}
            onDone={refetch}
            locale={locale}
          />
        )}
        {employeesEnterprise && (
          <CorporateEmployeesModal
            open
            enterprise={employeesEnterprise}
            onClose={() => setEmployeesEnterprise(null)}
            locale={locale}
          />
        )}
      </div>
    </SectionCard>
  );
};

export default CorporateListPage;
